

import { BellOutlined } from '@ant-design/icons';
import { Dropdown, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import React from 'react';

import WebSocket from 'src/services/WebSocket';
import { getAll, deleteAll, markAllAsRead } from 'src/apis/notifications';
import { useTranslation } from 'react-i18next';

import { getNotificationsSocketUrl } from 'src/utils/get-api-url';
import './style.scss';

import NotificationItem from './notificationItem';
import NotificationBadge from './notificationBadge';

export default function NotificationIcon() {
    const [notifications, setNotifications] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [loadNotifications, setLoadNotifications] = useState(false);
    const [showNotificationBadge, setShowNotificationBadge] = useState(false);
    
	const sessionUser = useSelector((state) => state.login.sessionUser);
    const { t } = useTranslation();

    function getTimeDifference(date) {
        const now = new Date();
        const diffMilliseconds = now - date;
        const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);
      
        if (diffDays > 0) {
            return diffDays + (diffDays === 1 ? ' d' : ' dd');
        } else if (diffHours > 0) {
            return diffHours + (diffHours === 1 ? ' h' : ' hh');
        } else if (diffMinutes > 0) {
            return diffMinutes + (diffMinutes === 1 ? ' m' : ' mm');
        } else {
            return `${diffMinutes/60} ss`
        }
    }

    function toCamelCase(str) {
        return str.toLowerCase().replace(/_([a-z])/g, function(_, letter) {
          return letter.toUpperCase();
        });
    }

    const getNotifications = async () => {
        const response = await getAll().catch(() => null);

        const allNotifications = (response?.data)
            ? response.data.map((n) => ({
                ...n,
                type: toCamelCase(n.type),
                createdAt: getTimeDifference(new Date(n.createdAt)),
            }))
            : [];

        setNotifications(allNotifications);
    };

    const onConfirmDelete = async () => {
        setDropdownOpen(false);
        setNotifications([]);
        await deleteAll();
    }

    const verifyNotifications = async () => {
        const response = await getAll().catch(() => null);

        const hasUnread = Array.isArray(response?.data) && response.data.some(n => !n.viewed);

        if (hasUnread) {
            setShowNotificationBadge(true);
        }
    }
    
    const connectSocket = () => {
        if (!sessionUser || !sessionUser.login) {
            return;
        }

        const onNewNotification = () => {
            setLoadNotifications(true)
        };

        const webSocket = new WebSocket(getNotificationsSocketUrl(), null)
        webSocket.subscribe(`/user/${sessionUser.login}/queue/notifications`, onNewNotification);

        webSocket.connect();
    }

    const onNewNotification = async () => {
        if (dropdownOpen) {
            await getNotifications();
        } else {
            setShowNotificationBadge(true);
        }

        setLoadNotifications(false);
    }

    const onDropdownClose = async () => {
        const unreadIds = notifications.filter(n => !n.viewed).map(n => n.id);

        if (unreadIds.length > 0) await markAllAsRead(unreadIds);
    }

    useEffect(() => {
        if (!sessionUser || !sessionUser.login) return;
        connectSocket();
        verifyNotifications();
    }, [sessionUser]);

    useEffect(() => {
        if (loadNotifications) {
            onNewNotification();
        }
    }, [loadNotifications])

    const setNotificationListOpen = async (open) => {
        if (open) {
            await getNotifications();
        } else {
            await onDropdownClose();
        }
        
        setDropdownOpen(open);

        if (showNotificationBadge) {
            setShowNotificationBadge(!showNotificationBadge);
        }
    }

    return (
        <Dropdown 
            trigger={['click']} 
            visible={dropdownOpen}
            className='notifications-list'
            onVisibleChange={setNotificationListOpen}
            overlay={<NotificationItem loadNotifications={loadNotifications} notifications={notifications} onDelete={onConfirmDelete} userLogin={sessionUser?.login} />} 
        >
            <Tooltip title={t('notifications.title')} placement='bottomRight' arrow={false}>
                <BellOutlined className="bell-icon" style={{ fontSize: 20, cursor: 'pointer' }} />
                {showNotificationBadge && <NotificationBadge />} 
            </Tooltip>
        </Dropdown>
    )
}